import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    type NetworkMember,
    networkMembersPath,
    useGetNetworkMembers,
} from "src/api/client-api/network/useNetworkLists";
import type { AppSearch } from "src/api/_common/hydra/search";
import { EntityListLayout } from "src/components/Grid";
import { AutoGrid } from "src/components/Grid/layout/AutoGrid";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useColumnsTableAction } from "src/components/Grid/headers/useColumnsTableAction";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import {
    NetworkEmptyState,
    NetworkError,
    type NetworkListProps,
    NetworkToolbar,
    tablePanelProps,
    toOptions,
    useNetworkExportAction,
} from "./networkListParts";
import { useNetworkMemberColumns } from "./useNetworkMemberColumns";
import { type NetworkListConfig, useNetworkListParams } from "./useNetworkListParams";

const GRID_KEY = "network-users-grid";

const SEARCH: AppSearch = {
    organization: { value: [] },
    parentOrganization: { value: [] },
    lastLoginAt: { value: "" },
    termsAcceptedAt: { value: "" },
};

const CONFIG: NetworkListConfig = {
    sortKeys: {
        lastname: "user.lastname",
        email: "user.email",
        organization: "organization.name",
        parentOrganization: "organization.parents",
        lastLoginAt: "user.lastLoginAt",
        termsAcceptedAt: "user.termsAcceptedAt",
    },
};

export const NetworkMembersList = (props: NetworkListProps) => {
    const { filterOptions } = props;
    const { t } = useTranslation();
    const { selectedCompany } = useFoodpilotContext();

    const organizations = useMemo(() => toOptions(filterOptions.organizations), [filterOptions]);
    const { params, filters, apiSort, pagination, search } = useNetworkListParams(CONFIG);
    const list = useGetNetworkMembers({ organizationId: selectedCompany?.id, params });

    const columns = useNetworkMemberColumns(organizations);
    const columnsConfig = useGridColumns(columns, GRID_KEY);
    const columnsAction = useColumnsTableAction(columnsConfig);
    const exportAction = useNetworkExportAction({
        path: networkMembersPath,
        fileNameStem: "network-users",
        filters,
        sort: apiSort,
        search: search.delayedSearch,
        totalItems: list.totalItems,
        entityLabel: t("network.export.users", "users"),
    });

    return (
        <EntityListLayout
            appearance="hub"
            ariaLabel={t("network.tabs.users", "Users")}
            gridHeader={
                <NetworkToolbar
                    search={search}
                    placeholder={t("network.users.search", "Search a user")}
                    actions={[columnsAction, exportAction]}
                />
            }
        >
            {list.errorStatus !== null ?
                <NetworkError status={list.errorStatus} onRetry={list.refetch} />
            :   <AutoGrid<NetworkMember>
                    appearance="hub"
                    mobileTableMode="scroll"
                    gridKey={GRID_KEY}
                    columns={columns}
                    columnsConfig={columnsConfig}
                    data={list.rows}
                    totalItems={list.totalItems}
                    isLoading={!list.isReady || list.isRefetching}
                    pagination={pagination}
                    searchParams={SEARCH}
                    enableRowActions={false}
                    tablePanelProps={tablePanelProps("users")}
                    renderEmptyState={({ resetFilters }) => (
                        <NetworkEmptyState
                            hasCriteria={Object.keys(filters).length > 0 || search.search !== ""}
                            onReset={() => {
                                resetFilters();
                                search.setSearch("");
                            }}
                        />
                    )}
                />
            }
        </EntityListLayout>
    );
};
